import { useCallback, useRef, useState } from "react";

/**
 * Pure selection-set transition: plain toggle, or shift-click range from the
 * anchor over the visible sorted key list. Returns the new set + new anchor.
 */
export const toggleSelection = (
  prev: Set<string>,
  anchor: string | null,
  key: string,
  visibleKeys: string[],
  shiftKey: boolean,
): { next: Set<string>; anchor: string } => {
  const next = new Set(prev);
  if (shiftKey && anchor) {
    const a = visibleKeys.indexOf(anchor);
    const b = visibleKeys.indexOf(key);
    if (a !== -1 && b !== -1) {
      for (let i = Math.min(a, b); i <= Math.max(a, b); i++) {
        next.add(visibleKeys[i]);
      }
      return { next, anchor: key };
    }
  }
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return { next, anchor: key };
};

/** Pure select-all/deselect-all over the visible keys only. */
export const setAllSelection = (
  prev: Set<string>,
  visibleKeys: string[],
  checked: boolean,
): Set<string> => {
  const next = new Set(prev);
  visibleKeys.forEach((k) => {
    if (checked) next.add(k);
    else next.delete(k);
  });
  return next;
};

/**
 * Checkbox selection over the visible sorted list. Keys are leadKey() strings
 * so selection survives re-sorts.
 */
export const useLeadSelection = () => {
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const anchorRef = useRef<string | null>(null);

  const toggle = useCallback(
    (key: string, visibleKeys: string[], shiftKey: boolean) => {
      setSelected((prev) => {
        const result = toggleSelection(
          prev,
          anchorRef.current,
          key,
          visibleKeys,
          shiftKey,
        );
        anchorRef.current = result.anchor;
        return result.next;
      });
    },
    [],
  );

  const setAll = useCallback((visibleKeys: string[], checked: boolean) => {
    setSelected((prev) => setAllSelection(prev, visibleKeys, checked));
  }, []);

  const clear = useCallback(() => {
    setSelected(new Set());
    anchorRef.current = null;
  }, []);

  return { selected, toggle, setAll, clear };
};
